/**
 * PD-35222 — placement-level attribution event helpers.
 *
 * Owns the canonical `placement_id` registry and the typed builders for the
 * `placement_viewed` and `placement_clicked` PostHog events. Every
 * placement-firing call site in discover_webapp must use the IDs and
 * builders exported here — that's what gives us stable IDs across deploys
 * and the schema enforcement Josh's weekly readout depends on.
 *
 * Schema (matches PD-35222 acceptance):
 *
 *   placement_viewed / placement_clicked {
 *     placement_id              // stable symbolic ID, from PLACEMENT_IDS below
 *     placement_type            // 'hero' | 'tombstone' | 'thank_you_module' | 'recommendation_tile' | 'campaign_card'
 *     placement_offering_slug   // the offering this placement features
 *     surface                   // 'homepage' | 'campaign_page' | 'thank_you' | 'invest_form' | 'explore'
 *     position                  // 1-based ordinal within the surface (1 = above the fold / top)
 *     experiment_key            // optional, when the placement is inside a running experiment
 *     variant                   // optional, when the placement is inside a running experiment
 *     click_target              // placement_clicked only — 'cta_button' | 'image' | 'card_body' | ...
 *   }
 */

import { safePostHogCapture } from 'utils/posthogHelpers';

/**
 * Canonical registry of `placement_id` values. Add a new entry here BEFORE
 * adding a fire site that references it. Lint convention (enforced by
 * code review): no string literal `placement_id` outside this file.
 *
 * Naming convention: `<surface>_<placement_type>[_<disambiguator>]`. Use
 * snake_case. Keep IDs stable across deploys — once an ID is in PostHog
 * insights, renaming it breaks dashboards.
 */
export const PLACEMENT_IDS = Object.freeze({
  HOMEPAGE_HERO: 'homepage_hero',
  HOMEPAGE_TOMBSTONE: 'homepage_tombstone',
  HOMEPAGE_RECOMMENDATION_TILE: 'homepage_recommendation_tile',
  CAMPAIGN_RAISE_OF_THE_DAY: 'campaign_raise_of_the_day',
  EXPLORE_TOMBSTONE: 'explore_tombstone',
  EXPLORE_RECOMMENDATION_TILE: 'explore_recommendation_tile',
  CAMPAIGN_PAGE_HERO: 'campaign_page_hero',
});

/**
 * Allowed `placement_type` values. Kept as a const set so the builders
 * can validate the caller hasn't passed a typo'd type.
 */
export const PLACEMENT_TYPES = Object.freeze({
  HERO: 'hero',
  TOMBSTONE: 'tombstone',
  THANK_YOU_MODULE: 'thank_you_module',
  RECOMMENDATION_TILE: 'recommendation_tile',
  CAMPAIGN_CARD: 'campaign_card',
});

/**
 * Allowed `surface` values. Same shape as the experimentation surface
 * enum — keep in lockstep with discover_webapp's other surface tags.
 */
export const PLACEMENT_SURFACES = Object.freeze({
  HOMEPAGE: 'homepage',
  CAMPAIGN_PAGE: 'campaign_page',
  THANK_YOU: 'thank_you',
  INVEST_FORM: 'invest_form',
  EXPLORE: 'explore',
});

/**
 * Allowed `click_target` values for `placement_clicked`. Optional — only
 * caller-relevant when a placement has multiple clickable regions and the
 * readout wants to split CTA-click vs card-body-click.
 */
export const CLICK_TARGETS = Object.freeze({
  CTA_BUTTON: 'cta_button',
  IMAGE: 'image',
  CARD_BODY: 'card_body',
  LOGO: 'logo',
  AMOUNT_FIELD: 'amount_field',
});

const KNOWN_PLACEMENT_IDS = new Set(Object.values(PLACEMENT_IDS));
const KNOWN_PLACEMENT_TYPES = new Set(Object.values(PLACEMENT_TYPES));
const KNOWN_SURFACES = new Set(Object.values(PLACEMENT_SURFACES));

/**
 * Build the common payload shared by `placement_viewed` and
 * `placement_clicked`. Returns `null` when any required field is missing
 * or invalid — callers MUST check the return and skip the emit, so the
 * readout never sees malformed rows.
 *
 * @param {object} args
 * @param {string} args.placement_id            One of PLACEMENT_IDS.*
 * @param {string} args.placement_type          One of PLACEMENT_TYPES.*
 * @param {string} args.placement_offering_slug The offering this placement features
 * @param {string} args.surface                 One of PLACEMENT_SURFACES.*
 * @param {number} args.position                1-based ordinal
 * @param {string} [args.experiment_key]        Optional experiment flag key
 * @param {string} [args.variant]               Optional variant ('control' | 'test' | ...)
 * @returns {object | null}
 */
const buildBasePayload = ({
  placement_id,
  placement_type,
  placement_offering_slug,
  surface,
  position,
  experiment_key,
  variant,
}) => {
  if (!KNOWN_PLACEMENT_IDS.has(placement_id)) return null;
  if (!KNOWN_PLACEMENT_TYPES.has(placement_type)) return null;
  if (!KNOWN_SURFACES.has(surface)) return null;
  if (typeof placement_offering_slug !== 'string' || !placement_offering_slug) {
    return null;
  }
  if (typeof position !== 'number' || !Number.isFinite(position) || position < 1) {
    return null;
  }

  return {
    placement_id,
    placement_type,
    placement_offering_slug,
    surface,
    position,
    experiment_key: experiment_key ?? null,
    variant: variant ?? null,
  };
};

/**
 * @typedef {object} PlacementArgs
 * @property {string} placement_id            One of PLACEMENT_IDS.*
 * @property {string} placement_type          One of PLACEMENT_TYPES.*
 * @property {string} placement_offering_slug The offering this placement features
 * @property {string} surface                 One of PLACEMENT_SURFACES.*
 * @property {number} position                1-based ordinal
 * @property {string|null} [experiment_key]   Optional experiment flag key
 * @property {string|null} [variant]          Optional variant ('control' | 'test' | ...)
 */

/**
 * @typedef {PlacementArgs & { click_target?: string|null }} PlacementClickArgs
 */

/**
 * Fire `placement_viewed`. Skips the emit when the payload is invalid
 * (returns false). Callers typically wire this to an IntersectionObserver
 * with a once-per-mount ref so the same placement doesn't fire twice in
 * a session.
 *
 * @param {PlacementArgs} args
 * @returns {boolean} true if the event was emitted, false on validation failure
 */
export const firePlacementViewed = (args) => {
  const payload = buildBasePayload(args);
  if (!payload) return false;
  return safePostHogCapture('placement_viewed', payload);
};

/**
 * Fire `placement_clicked`. Same payload as `placement_viewed` plus an
 * optional `click_target` for placements with multiple clickable regions.
 *
 * @param {PlacementClickArgs} args
 * @returns {boolean} true if the event was emitted, false on validation failure
 */
export const firePlacementClicked = ({ click_target, ...rest }) => {
  const payload = buildBasePayload(rest);
  if (!payload) return false;
  return safePostHogCapture('placement_clicked', {
    ...payload,
    click_target: click_target ?? null,
  });
};
